/**
 * Authentication Service - JWT-based authentication for Azure deployment
 * Handles all authentication-related API calls for Scott Overhead Doors
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/apiConfig';

class AuthService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.isLoggingOut = false;
    this.logoutCompletedAt = null;
    this.LOGOUT_PROTECTION_TIME = 3000; // 3 seconds
    this.isRefreshing = false;
    this.refreshPromise = null;

    // JWT token storage keys
    this.ACCESS_TOKEN_KEY = 'scott_access_token';
    this.REFRESH_TOKEN_KEY = 'scott_refresh_token';
    this.USER_DATA_KEY = 'currentUser';

    // In local development, API_BASE_URL is empty to use proxy - this is expected
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!this.baseURL && !isLocalDev) {
      console.error('API_BASE_URL is not configured. Check your apiConfig.js file.');
      throw new Error('API configuration is missing. Unable to initialize AuthService.');
    }


  }

  /**
   * Get stored JWT access token
   */
  getAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get stored JWT refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Store JWT tokens securely
   * Enhanced to ensure both tokens are properly stored
   */
  storeTokens(tokens) {
    if (tokens.access_token) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);

    }
    if (tokens.refresh_token) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);

    }
    
    // Also handle alternative response formats from server
    if (tokens.access) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access);

    }
    if (tokens.refresh) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh);

    }
  }

  /**
   * Clear all stored tokens and user data
   */
  clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    // Reset refresh state
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Make authenticated API request with JWT token handling
   * Enhanced with better refresh token logic
   */
  async makeRequest(endpoint, options = {}) {
    // CRITICAL FIX: Check if endpoint already has /api prefix to avoid duplication
    // If the endpoint already starts with /api, use it as-is
    // Otherwise, add /api prefix for auth endpoints
    let url;
    
    if (endpoint.startsWith('/api/')) {
      // Endpoint already has /api prefix, use as-is
      url = `${this.baseURL}${endpoint}`;
    } else if (endpoint.includes('/auth/')) {
      // Auth endpoint without /api prefix, add it
      url = `${this.baseURL}/api${endpoint}`;
    } else {
      // Other endpoints, use as provided
      url = `${this.baseURL}${endpoint}`;
    }
    
    // Get the access token
    const accessToken = this.getAccessToken();
    
    // Build request headers
    const headers = {
      'Accept': 'application/json',
      ...options.headers,
    };

    // Add Content-Type for non-GET requests
    if (options.method && options.method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    // Add Authorization header if we have a token
    // Skip for login and refresh endpoints
    if (accessToken && !endpoint.includes('/login') && !endpoint.includes('/refresh')) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const defaultOptions = {
      method: options.method || 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: headers,
      credentials: 'include', // Still include for session cookie compatibility
      ...options,
    };

    try {

      const response = await fetch(url, defaultOptions);
      

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();

          return data;
        }
        return { success: true };
      } else {
        // Handle 401 errors - token might be expired
        if (response.status === 401 && 
            !endpoint.includes('/refresh') && 
            !endpoint.includes('/login') &&
            !endpoint.includes('/logout')) {
          

          // Try to refresh the token
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry the original request with new token
            const newToken = this.getAccessToken();
            if (newToken) {
              defaultOptions.headers['Authorization'] = `Bearer ${newToken}`;

              const retryResponse = await fetch(url, defaultOptions);
              
              if (retryResponse.ok) {
                const contentType = retryResponse.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                  return await retryResponse.json();
                }
                return { success: true };
              }
            }
          } else {
            // Refresh failed, clear auth data and throw error
            this.handleUnauthorized();
            throw new Error('Authentication required. Please log in again.');
          }
        }
        
        // Handle other error responses
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error(`Request failed with status ${response.status}:`, errorData);
        
        if (response.status === 401) {
          this.handleUnauthorized();
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access forbidden. You do not have permission to perform this action.');
        } else if (response.status === 404) {
          // Don't throw error for 404 on verify/refresh endpoints - just return false
          if (endpoint.includes('/verify') || endpoint.includes('/refresh')) {
            return null;
          }
          throw new Error('Requested resource not found. Please check the URL or contact support.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later or contact support.');
        }
        
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Request error details:', {
        name: error.name,
        message: error.message,
        url: url,
        method: defaultOptions.method
      });
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error detected:', error);
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      
      throw error;
    }
  }

  // In authService.js, update the refreshAccessToken method:

  async refreshAccessToken() {
      // Prevent multiple simultaneous refresh attempts
      if (this.isRefreshing && this.refreshPromise) {

          return this.refreshPromise;
      }

      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {

          return false;
      }
      
      this.isRefreshing = true;
      
      // Create the refresh promise that other calls can wait on
      this.refreshPromise = (async () => {
          try {
              const refreshUrl = `${this.baseURL}/api/auth/refresh`;

              // Make a direct fetch call for refresh to avoid circular dependency
              const response = await fetch(refreshUrl, {
                  method: 'POST',
                  mode: 'cors',
                  cache: 'no-cache',
                  credentials: 'include',
                  headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json',
                  },
                  body: JSON.stringify({ 
                      refresh_token: refreshToken
                  }),
              });
              
              if (response.ok) {
                  const data = await response.json();

                  // Handle different response formats
                  if (data.access_token || data.tokens?.access_token) {
                      const newTokens = {
                          access_token: data.access_token || data.tokens?.access_token,
                          refresh_token: data.refresh_token || data.tokens?.refresh_token || refreshToken
                      };
                      
                      this.storeTokens(newTokens);
                      
                      // Update user data if provided
                      if (data.user) {
                          localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(data.user));
                      }
                      

                      return true;
                  }
                  
                  console.error('Unexpected refresh response format:', data);
                  return false;
              } else {
                  // Refresh failed
                  let errorData;
                  try {
                      errorData = await response.json();
                  } catch {
                      errorData = { error: `Refresh failed with status ${response.status}` };
                  }
                  
                  console.error('Token refresh failed:', errorData);
                  
                  // If refresh token is invalid or expired, clear all auth data
                  if (response.status === 401 || response.status === 403) {

                      this.clearTokens();
                  }
                  
                  return false;
              }
          } catch (error) {
              console.error('Failed to refresh token:', error);
              // Only clear tokens if it's an auth error, not a network error
              if (!error.message.includes('fetch') && !error.message.includes('network')) {
                  this.clearTokens();
              }
              return false;
          } finally {
              this.isRefreshing = false;
              this.refreshPromise = null;
          }
      })();
      
      return this.refreshPromise;
  }

  /**
   * Handle unauthorized responses
   */
  handleUnauthorized() {

    this.clearAuthData();
  }

  /**
   * Login user with JWT token support
   * Enhanced to ensure tokens are properly stored
   */
  async login(username, password) {
    try {

      // Validate inputs
      if (!username || !password) {
        throw new Error('Username and password are required');
      }
      
      // Clear logout state when logging in
      this.isLoggingOut = false;
      this.logoutCompletedAt = null;
      localStorage.removeItem('lastLogoutTime');
      localStorage.removeItem('authBlocked');
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password }),
      });

      if (response && response.user) {

        // Store JWT tokens if provided in various possible formats
        if (response.tokens) {
          this.storeTokens(response.tokens);

        } else if (response.access_token || response.refresh_token) {
          // Tokens might be directly in the response
          this.storeTokens({
            access_token: response.access_token,
            refresh_token: response.refresh_token
          });

        } else if (response.access || response.refresh) {
          // Alternative naming
          this.storeTokens({
            access_token: response.access,
            refresh_token: response.refresh
          });

        } else {
          console.warn('No tokens found in login response. Server might not be configured for JWT.');
        }
        
        // Store user data
        localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(response.user));
        
        // Verify tokens were stored
        const storedAccess = this.getAccessToken();
        const storedRefresh = this.getRefreshToken();
        return response;
      }
      
      throw new Error('Login failed: Invalid response from server');
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.message.includes('Unable to connect') || 
          error.message.includes('Server connection error')) {
        throw new Error('Cannot connect to the server. Please check your internet connection and try again.');
      }
      
      if (error.message.includes('401') || error.message.includes('Authentication')) {
        throw new Error('Invalid username or password. Please check your credentials and try again.');
      }
      
      if (error.message.includes('403')) {
        throw new Error('Access denied. Please contact support.');
      }
      
      if (error.message.includes('500') || error.message.includes('Server error')) {
        throw new Error('Server is currently unavailable. Please try again later.');
      }
      
      throw error;
    }
  }

  /**
   * Logout with JWT token cleanup
   */
  async logout() {
    try {

      // Set logout flags immediately
      this.isLoggingOut = true;
      localStorage.setItem('authBlocked', 'true');
      localStorage.setItem('lastLogoutTime', Date.now().toString());
      
      const userData = this.getStoredUser();
      const username = userData ? userData.username : 'unknown';

      // Clear local data first to ensure user is logged out locally
      this.clearAuthData();
      
      // Try server logout with timeout
      try {
        const logoutPromise = this.makeRequest(API_ENDPOINTS.AUTH.LOGOUT, { method: 'POST' });
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Logout request timeout')), 10000)
        );
        
        await Promise.race([logoutPromise, timeoutPromise]);

      } catch (serverError) {
        console.warn('Server logout failed (local logout succeeded):', serverError.message);
        // Don't throw error here - local logout is sufficient
      }
      
      this.logoutCompletedAt = Date.now();

      return { success: true, message: 'Logout successful' };
      
    } catch (error) {
      console.error('Logout error, forcing local cleanup:', error);
      this.clearAuthData();
      this.logoutCompletedAt = Date.now();
      
      return { 
        success: true, 
        message: 'Logout completed', 
        warning: 'Local logout completed but server communication failed'
      };
    } finally {
      // Keep logout protection active for security
      setTimeout(() => {
        this.isLoggingOut = false;
        localStorage.removeItem('authBlocked');

      }, this.LOGOUT_PROTECTION_TIME);
    }
  }

  /**
   * Clear authentication data including JWT tokens
   */
  clearAuthData() {
    try {
      // Clear JWT tokens
      this.clearTokens();
      
      // Clear other auth-related data
      const keysToRemove = [
        'authToken', 
        'sessionData',
        'userCredentials',
        'remember_token',
        'auth_session',
        'user_session',
        'access_token',
        'refresh_token',
        'jwt_token'
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(key);
        }
      });
      
      // Clear any cookies related to authentication
      if (document.cookie) {
        const cookies = document.cookie.split(';');
        cookies.forEach(cookie => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name.toLowerCase().includes('auth') || 
              name.toLowerCase().includes('session') ||
              name.toLowerCase().includes('token')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
      }
      

    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  }

  /**
   * Check if authentication is blocked
   */
  isAuthenticationBlocked() {
    if (this.isLoggingOut) {

      return true;
    }

    if (localStorage.getItem('authBlocked') === 'true') {

      return true;
    }

    if (this.logoutCompletedAt) {
      const timeSinceLogout = Date.now() - this.logoutCompletedAt;
      if (timeSinceLogout < this.LOGOUT_PROTECTION_TIME) {

        return true;
      }
      this.logoutCompletedAt = null;
    }

    const logoutTimestamp = localStorage.getItem('lastLogoutTime');
    if (logoutTimestamp) {
      const timeSinceLogout = Date.now() - parseInt(logoutTimestamp);
      if (timeSinceLogout < this.LOGOUT_PROTECTION_TIME) {

        return true;
      }
      localStorage.removeItem('lastLogoutTime');
    }

    return false;
  }

  /**
   * Validate session with JWT token verification
   * Enhanced with better token refresh logic
   */
  async validateSession() {
    try {
      if (this.isAuthenticationBlocked()) {

        return false;
      }
      
      // Check if we have an access token
      const accessToken = this.getAccessToken();
      if (!accessToken) {

        return false;
      }
      

      // CRITICAL FIX: Use /auth/me endpoint which exists instead of /auth/verify
      try {
        const response = await this.makeRequest(API_ENDPOINTS.AUTH.ME, {
          method: 'GET'
        });
        
        if (response && response.id) {
          // Valid session
          localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(response));

          return true;
        }
        

        return false;
      } catch (error) {

        // If it's a 401 error, the makeRequest method should have already tried to refresh
        if (error.message.includes('Authentication required')) {
          return false;
        }
        
        // For other errors (network, server errors), we might still be authenticated
        // Return true if we have tokens, false otherwise
        const hasTokens = !!this.getAccessToken() && !!this.getRefreshToken();
        return hasTokens;
      }
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser() {
    try {
      if (this.isAuthenticationBlocked()) {

        return null;
      }
      
      // Check if we have a token
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        return null;
      }
      
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.ME);
      
      if (response && response.id) {
        localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(response));
        return response;
      }
      

      return null;
    } catch (error) {
      console.error('getCurrentUser failed:', error);
      
      // Don't clear everything for network errors, only for auth errors
      if (error.message.includes('Authentication required') || 
          error.message.includes('401')) {
        this.clearAuthData();
      }
      
      return null;
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    try {
      if (!currentPassword || !newPassword) {
        throw new Error('Current password and new password are required');
      }
      
      if (newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters long');
      }
      
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      
      return response;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   * Enhanced to check both tokens
   */
  isAuthenticated() {
    if (this.isAuthenticationBlocked()) {

      return false;
    }
    
    // Check for JWT tokens
    const hasAccessToken = !!this.getAccessToken();
    const hasRefreshToken = !!this.getRefreshToken();
    
    // Also check for user data
    const userData = localStorage.getItem(this.USER_DATA_KEY);
    const hasUserData = userData !== null && userData !== 'null';
    
    // We're authenticated if we have at least an access token or refresh token AND user data
    const isAuth = (hasAccessToken || hasRefreshToken) && hasUserData;
    
    return isAuth;
  }

  /**
   * Get stored user data
   */
  getStoredUser() {
    try {
      if (this.isAuthenticationBlocked()) {

        return null;
      }
      
      const userData = localStorage.getItem(this.USER_DATA_KEY);
      if (!userData || userData === 'null') {
        return null;
      }
      
      const parsed = JSON.parse(userData);
      
      // Validate that the user object has required fields
      if (!parsed || !parsed.id || !parsed.username) {
        console.warn('Invalid user data structure found, clearing...');
        localStorage.removeItem(this.USER_DATA_KEY);
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.error('Error getting stored user:', error);
      localStorage.removeItem(this.USER_DATA_KEY);
      return null;
    }
  }

  /**
   * Check if current user has admin role
   */
  isAdmin() {
    const user = this.getStoredUser();
    return user && user.role === 'admin';
  }

  /**
   * Check if current user has field role
   */
  isField() {
    const user = this.getStoredUser();
    return user && user.role === 'field';
  }

  // User Management Methods (Admin only)
  async getUsers() {
    try {
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.USERS);
      return response;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      if (!userData || !userData.username || !userData.email) {
        throw new Error('Username and email are required to create a user');
      }
      
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.USERS, {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  async updateUser(userId, userData) {
    try {
      if (!userId) {
        throw new Error('User ID is required to update a user');
      }
      
      const response = await this.makeRequest(`${API_ENDPOINTS.AUTH.USERS}/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required to delete a user');
      }
      
      const response = await this.makeRequest(`${API_ENDPOINTS.AUTH.USERS}/${userId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  async resetUserPassword(userId, newPassword) {
    try {
      if (!userId || !newPassword) {
        throw new Error('User ID and new password are required');
      }
      
      if (newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters long');
      }
      
      const response = await this.makeRequest(API_ENDPOINTS.AUTH.RESET_PASSWORD(userId), {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword }),
      });
      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
